import { useEffect, useState } from "react"
import { GetApp } from "../../App"
import { Block, BlockContainer, BlockField, BlockRow } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { Overlay, OverlayNoClose, OverlayTitle } from "../../Components/Overlay"

interface AppDisconnectedViewProps {
  connected: boolean;
}

export function AppDisconnectedView({ connected }: AppDisconnectedViewProps) {
  const app = GetApp()!;
  const [disconnected, setDisconnected] = useState<boolean>(false);
  // Latched alongside `disconnected`: once the connection has come back at least
  // once, the reload action stays available even if the socket drops again (#558).
  // The socket flaps while the server -- or, on a native device, the shell this
  // very disconnect asked to restart (`AppView`'s socket subscription) -- comes
  // back up: it opens, then closes again a few hundred ms later. A reload gate
  // following that flag live means a user tapping the just-enabled button either
  // taps a button React has already re-rendered as `disabled` (the browser never
  // dispatches the click at all) or has the reload swallowed by a second check
  // inside the handler -- both look exactly like "the button does nothing", while
  // a plain browser refresh always works.
  const [reloadable, setReloadable] = useState<boolean>(false);
  const [reloading, setReloading] = useState<boolean>(false);
  const t = app.getT("AppDisconnectedView");

  useEffect(() => {
    if (!connected) {
      setDisconnected(true);
    } else {
      setReloadable(true);
    }
  }, [connected]);

  // The reloading state is only feedback for the click -- a real reload takes the
  // whole document with it. If it somehow doesn't take (which is the very failure
  // this issue is about), re-offer the button instead of leaving the user with a
  // permanently disabled one: this overlay has no other way out.
  useEffect(() => {
    if (!reloading) {
      return;
    }
    const timer = window.setTimeout(() => setReloading(false), 5000);
    return () => window.clearTimeout(timer);
  }, [reloading]);

  if (!disconnected || app.cloudSession()) {
    return;
  }

  return (
    <Overlay closeCallback={OverlayNoClose} hideClose={true}>
      <OverlayTitle style="alarm" title={t("connection_overlay_title")} />

      <BlockContainer className="Overlay-body">
        <Block>
          <BlockRow>
            <LocalizedMessage message={(m) => connected ? m.connection.recovered : m.connection.interrupted} />
          </BlockRow>
          <BlockRow actions={true}>
            {app.localSession() && (
              <BlockField>
                <button className="button" onClick={() => {
                  app.logout(false);
                }}
                >{t("disconnect_companion")}</button>
              </BlockField>
            )}
            <BlockField>
              <button disabled={!reloadable || reloading} className="button default" onClick={() => {
                // Unconditional: the click itself is the user's decision to reload,
                // and re-checking the live connection here is what made the button
                // dead in the flapping case above. A reload is served over plain
                // HTTP and is the guaranteed way out of this overlay.
                setReloading(true);
                window.location.reload();
              }}
              >{reloading ? t("connection_overlay_reloading") : t("connection_overlay_reload")}</button>
            </BlockField>
          </BlockRow>
        </Block>
      </BlockContainer>
    </Overlay>
  );
}
